// Nexora (nexora-app) — moteur de synchronisation de la file d'attente hors-ligne.
// Port quasi verbatim de StoreFlowProV2/public/offline-sync.js — rejoue les actions en
// attente (offlineOutbox.js) en FIFO dès que le réseau revient, affiche un badge de statut
// discret (DOM pur, hors de l'arbre React, comme côté vanilla — aucun conflit).
export function installOfflineSync() {
  if (window.__nxSyncInstalled) return;
  window.__nxSyncInstalled = true;
  if (!window.NexoraOutbox) return; // offlineOutbox.js pas installé

  const MAX_ATTEMPTS = 3;
  const POLL_MS = 30000;
  let syncing = false;

  function buildRequestInit(entry) {
    const init = { method: entry.method };
    if (entry.body != null) init.body = entry.body;
    if (entry.headers && Object.keys(entry.headers).length) init.headers = entry.headers;
    return init;
  }

  async function replayOne(entry) {
    const doFetch = window.NexoraOutbox.fetchWithTimeout || window.NexoraOutbox.originalFetch;
    try {
      const res = await doFetch(entry.url, buildRequestInit(entry));
      if (res.ok) {
        await window.NexoraOutbox.remove(entry.id);
        return "ok";
      }

      // Réponse serveur (pas un échec réseau) : l'action a été rejetée, pas juste différée.
      const attempts = (entry.attempts || 0) + 1;
      const data = await res.json().catch(() => ({}));
      if (attempts >= MAX_ATTEMPTS) {
        await window.NexoraOutbox.patch(entry.id, { status: "failed", attempts, lastError: data.error || `HTTP ${res.status}` });
      } else {
        await window.NexoraOutbox.patch(entry.id, { attempts, lastError: data.error || `HTTP ${res.status}` });
      }
      return "rejected";
    } catch (networkError) {
      return "network-down"; // toujours hors-ligne : on arrête le rejeu, on réessaiera plus tard
    }
  }

  async function trySync() {
    if (syncing || !navigator.onLine) return;
    syncing = true;
    updateBadge("syncing");

    try {
      const entries = await window.NexoraOutbox.listAll();
      const pending = entries.filter((e) => e.status !== "failed");

      for (const entry of pending) {
        const result = await replayOne(entry);
        if (result === "network-down") break; // on garde l'ordre FIFO, on s'arrête là
      }
    } finally {
      syncing = false;
      updateBadge();
    }
  }

  // --- Badge de statut ---------------------------------------------------
  let badgeEl = null;

  function ensureBadge() {
    if (badgeEl) return badgeEl;
    badgeEl = document.createElement("button");
    badgeEl.id = "nxSyncBadge";
    badgeEl.type = "button";
    badgeEl.style.cssText = [
      "position:fixed", "right:14px", "bottom:14px", "z-index:99998",
      "background:#0d1730", "color:#fff", "border:none", "padding:8px 14px",
      "border-radius:999px", "font:13px/1.3 system-ui,sans-serif",
      "box-shadow:0 6px 18px rgba(0,0,0,.22)", "display:none", "cursor:pointer"
    ].join(";");
    badgeEl.addEventListener("click", () => trySync());
    document.body.appendChild(badgeEl);
    return badgeEl;
  }

  async function updateBadge(state) {
    const el = ensureBadge();
    const entries = await window.NexoraOutbox.listAll();
    const pendingCount = entries.filter((e) => e.status === "pending").length;
    const failedCount = entries.filter((e) => e.status === "failed").length;

    if (state === "syncing") {
      el.style.display = "block";
      el.textContent = "🔄 Synchronisation...";
      return;
    }

    if (failedCount > 0) {
      el.style.display = "block";
      el.textContent = `⚠️ ${failedCount} action${failedCount > 1 ? "s" : ""} non synchronisée${failedCount > 1 ? "s" : ""} — réessayer`;
      return;
    }

    if (pendingCount > 0) {
      el.style.display = "block";
      el.textContent = (navigator.onLine ? "🔄 " : "📴 Hors-ligne — ") + `${pendingCount} en attente`;
      return;
    }

    el.style.display = "none";
  }

  window.addEventListener("nx-outbox-change", () => updateBadge());
  window.addEventListener("online", trySync);
  window.addEventListener("offline", () => updateBadge());
  window.addEventListener("load", () => {
    updateBadge();
    if (navigator.onLine) trySync();
  });
  setInterval(trySync, POLL_MS);

  window.NexoraSync = { trySync };
}
